'use client';

import { DataTableColumnHeader } from '@components/table';
import { RiskIndicatorTag } from '@components/ui';
import { getLimitsForIndicator, MapperHelper, RiskIndicatorKey } from '@helpers';
import { riskIndicators } from '@lib';
import { Checkbox } from '@registry/new-york/ui/checkbox';
import { CommentCell } from '@sections/UnweightedValuesTable/_components/Comments/CommentCell/CommentCell';
import { ScoreBar, ScoreInterval } from '@sections/UnweightedValuesTable/_components/ScoreBar/ScoreBar';
import { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import React from 'react';

import styles from './Columns.module.scss';

export interface UnweightedScoreRow {
  riskIndicator: string;
  metric: string;
  unweightedRisk: number;
  risk?: number;
  weightedRisk?: number;
  contribution?: number;
  comment?: string;
  riskMetricResultId?: number;
  isApplicable?: boolean;
  scoreColor?: string;
  confidenceLow?: number | null;
  confidenceHigh?: number | null;
}

interface ColumnsOptions {
  onEditComment: (row: UnweightedScoreRow) => void;
  onDeleteComment: (row: UnweightedScoreRow) => void;
  onMetricClick?: (metricName: string) => void;
  indicatorLimits?: Partial<Record<RiskIndicatorKey, string>>;
}

const buildInterval = (low?: number | null, high?: number | null, maxValue = 1): ScoreInterval | undefined => {
  if (low == null || high == null || Number.isNaN(low) || Number.isNaN(high)) return undefined;
  const lo = Math.min(Math.max(low / maxValue, 0), 1);
  const hi = Math.min(Math.max(high / maxValue, 0), 1);
  return { lo, hi, label: `${low.toFixed(2)}\u2009\u2013\u2009${high.toFixed(2)}` };
};

export const getColumns = ({
  onEditComment,
  onDeleteComment,
  onMetricClick,
  indicatorLimits,
}: ColumnsOptions): ColumnDef<UnweightedScoreRow>[] => [
  {
    accessorKey: 'isApplicable',
    header: ({ column, table }) => {
      const visibleRows = table.getRowModel().rows;
      const allVisibleRowsSelected = visibleRows.length > 0 && visibleRows.every(row => row.original.isApplicable);

      return (
        <div className={styles.applicableHeader}>
          <Checkbox
            checked={allVisibleRowsSelected}
            onCheckedChange={value => {
              table.toggleAllPageRowsSelected(!!value);
              if ((table as any).options.meta?.toggleAllApplicable) {
                (table as any).options.meta.toggleAllApplicable(!!value);
              }
            }}
            aria-label="Mark all as applicable"
            className="data-[state=checked]:bg-[#628fb9] border border-[#3d5a80]"
          />
          <DataTableColumnHeader
            column={column}
            title="Applicable"
            className={styles.nonClickableHeader}
          />
        </div>
      );
    },
    cell: ({ row, table }) => (
      <Checkbox
        checked={row.getValue('isApplicable')}
        onCheckedChange={() =>
          (table as any).options.meta.toggleApplicable(row.original, !row.getValue('isApplicable'))
        }
        aria-label="Select row"
        className={styles.applicableCheckbox}
      />
    ),
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'riskIndicator',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Risk indicator"
      />
    ),
    cell: ({ row }) => (
      <span className={clsx(styles.row, styles.fontWeight500)}>
        <RiskIndicatorTag
          indicator={riskIndicators.find(i => i.name === row.getValue('riskIndicator'))}
          className={styles.riskIndicatorCard}
          tooltipClassName={styles.metricTooltip}
          disableTooltip
        />
      </span>
    ),
    enableHiding: false,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'metric',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Metric"
      />
    ),
    cell: ({ row }) =>
      onMetricClick ? (
        <button
          type="button"
          className={clsx(styles.row, styles.fontWeight500, styles.metricLink)}
          onClick={() => onMetricClick(row.getValue('metric'))}
        >
          {row.getValue('metric')}
        </button>
      ) : (
        <span className={clsx(styles.row, styles.fontWeight500)}>{row.getValue('metric')}</span>
      ),
    enableHiding: false,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'unweightedRisk',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Score"
      />
    ),
    cell: ({ row }) => (
      <div className={clsx(styles.row, styles.fontWeight500, styles.scoreCell)}>
        <ScoreBar
          value={row.getValue('unweightedRisk')}
          maxValue={1}
          limits={getLimitsForIndicator(row.original.riskIndicator, indicatorLimits)}
          showLabel
          interval={buildInterval(row.original.confidenceLow, row.original.confidenceHigh)}
        />
      </div>
    ),
    enableHiding: false,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'comment',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Comment"
      />
    ),
    cell: ({ row }) => {
      const limits = getLimitsForIndicator(row.original.riskIndicator, indicatorLimits);
      const percentage = Math.min(Math.max(row.original.unweightedRisk, 0), 1) * 100;
      const rowWithColor = { ...row.original, scoreColor: MapperHelper.getRiskIndicatorColor(percentage, limits) };

      return (
        <CommentCell
          comment={row.original.comment}
          metric={row.original.metric}
          onEditComment={() => onEditComment(rowWithColor)}
          onDeleteComment={() => onDeleteComment(row.original)}
        />
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
];
